import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import Cookies from 'js-cookie'
import CurrencyService from '@/services/CurrencyService'
import { captureException } from '@/utils/SentryUtils'
import type { Currency } from '@/types/Currency'
import { registerCurrencies } from '@/utils/tool.js'
import { setActiveCurrency } from '@/utils/currencyState'

const CURRENCY_COOKIE_KEY = 'currency'
// PCI DSS (TTGP-1590): HTTPS only. It cannot be HttpOnly because this store reads and writes it, and PHP also reads it.
const CURRENCY_COOKIE_SECURE = { secure: true } as const
const DEFAULT_CURRENCY = 'USD'

const countryToCurrency: Record<string, string> = {
  US: 'USD',
  CA: 'CAD',
  CN: 'CNY',
  GB: 'GBP',
  EU: 'EUR',
  DE: 'EUR',
  FR: 'EUR',
  AU: 'AUD',
}



export const useCurrencyStore = defineStore('currency', () => {
  const currentCurrency = ref<string>(Cookies.get(CURRENCY_COOKIE_KEY) ?? '')
  // Keep the GraphQL header (services/ServiceBase.ts) on the currency the page is actually showing.
  watch(currentCurrency, (code) => setActiveCurrency(code), { immediate: true })
  const currencyList = ref<Currency[]>([])
  const loading = ref(false)
  const loaded = ref(false)
  // Once a page (e.g. a product detail page) or the user has explicitly picked a
  // currency, the background session sync in fetchCurrencies() must not clobber it -
  // whichever of the two async calls happens to resolve last would otherwise win.
  const currencyOverridden = ref(false)
  // Order-flow pages (details, booking, flight, insurance) lock the currency to
  // whatever the order is priced in - the switcher UI must not be able to change it,
  // but code (setDisplayCurrency) still can, e.g. to sync it to the order's currency.
  const locked = ref(false)
  // Callers (header's CurrencySelector, order-flow pages, etc.) can all mount around the
  // same time and call fetchCurrencies() concurrently. Sharing the in-flight promise means
  // every caller actually waits for the same fetch to land, instead of the loading.value
  // guard letting later callers return immediately while the first fetch is still pending.
  let fetchPromise: Promise<void> | null = null

  function lockCurrency(lock=true) {
    locked.value = lock
  }

  // The IP lookup happens on the server (api/geo/country): the ip-api key stays out of the browser bundle.
  async function fetchCountryByIp(): Promise<string> {
    try {
      return (await CurrencyService.getGeoByIp()).country
    } catch (err) {
      captureException(err, { tags: { store: 'CurrencyStore', action: 'fetchCountryByIp' } })
      return ''
    }
  }

  // Runs in the background so the store's setup stays sync (useCurrencyStore() is
  // called synchronously all over the app - an async setup function would force
  // every call site to `await` it). Only applies once cookie/IP resolve; if the
  // user or a page already picked a currency by then, don't clobber it.
  async function resolveCurrencyByIp() {
    const countryCode = await fetchCountryByIp()
    let targetCurrency = DEFAULT_CURRENCY

    if (countryCode) {
      const upperCode = countryCode.toUpperCase()
      targetCurrency = countryToCurrency[upperCode] ?? DEFAULT_CURRENCY      
    }
    if (currencyOverridden.value) return
    currentCurrency.value = targetCurrency
    Cookies.set(CURRENCY_COOKIE_KEY, targetCurrency, { expires: 30, ...CURRENCY_COOKIE_SECURE })
  }

  if (!currentCurrency.value) 
  {
    resolveCurrencyByIp()
  }

  async function fetchCurrencies() {
    if (loaded.value) return
    if (fetchPromise) return fetchPromise
    loading.value = true
    fetchPromise = (async () => {
      try {
        const { currencies, current } = await CurrencyService.getCurrencies()
        currencyList.value = currencies
        registerCurrencies(currencies)
        if (current && !currencyOverridden.value) {
         // currentCurrency.value = current
         // Cookies.set(CURRENCY_COOKIE_KEY, current, { expires: 300 })
        }
        loaded.value = true
      } catch (error) {
        captureException(error, { tags: { store: 'CurrencyStore', action: 'fetchCurrencies' } })
      } finally {
        loading.value = false
        fetchPromise = null
      }
    })()
    return fetchPromise
  }

  // Non-persisted override for page-driven display currency (e.g. a product's native
  // currency on its detail page). Does not touch the cookie or backend session - it's
  // purely what this page renders with.
  function setDisplayCurrency(code: string) {
    if (!code) return
    currencyOverridden.value = true
    currentCurrency.value = code
   // Cookies.set(CURRENCY_COOKIE_KEY, code, { expires: 300 })
  }

  async function setCurrency(code: string) {
    if (locked.value) return
    currencyOverridden.value = true
    if (code === currentCurrency.value) return
    const previous = currentCurrency.value
    currentCurrency.value = code
    Cookies.set(CURRENCY_COOKIE_KEY, code, { expires: 300, ...CURRENCY_COOKIE_SECURE })
    try {
      
      await CurrencyService.setCurrency(code)
    } catch (error) {
      currentCurrency.value = previous
      Cookies.set(CURRENCY_COOKIE_KEY, previous, { expires: 300, ...CURRENCY_COOKIE_SECURE })
      captureException(error, { tags: { store: 'CurrencyStore', action: 'setCurrency' } })
      throw error
    }
    // Deliberately no page reload here: setCurrency() is also used to
    // programmatically sync the session currency to an order's/booking's own
    // currency mid-flow (cart.vue, user/bookings), immediately followed by
    // lockCurrency(true) - reloading there would interrupt cart/checkout/booking
    // view state. Callers that represent a genuine user-initiated currency change
    // (the header switcher) are responsible for reloading themselves.
  }

  return {
    currentCurrency,
    currencyList,
    loading,
    locked,
    fetchCurrencies,
    setCurrency,
    setDisplayCurrency,
    lockCurrency
  }
})
